/**
 * BookingFlows Pricing Configuration
 * 
 * SINGLE SOURCE OF TRUTH for all pricing across the app
 * Update this file to change pricing everywhere
 */

export const PRICING_CONFIG = {
  // Free Trial
  trial: {
    id: 'trial',
    name: 'Free Trial',
    price: 0,
    period: '30 days',
    description: 'Try all features free',
    popular: false,
    features: [
      'Up to 5 teamMembers',
      'Up to 5 assets',
      'Unlimited bookings',
      '25 free automation actions',
      '🤖 AI Auto-Scheduler',
      '💰 Payroll Calculator',
      '📊 Basic Reports',
      '👥 Member Self-Service View'
    ],
    cta: 'Start Free Trial',
    limits: {
      team: 5,
      assets: 5,
      charters: 'unlimited',
      actions: 25
    }
  },

  // Starter Plan
  starter: {
    id: 'starter',
    name: 'Starter',
    price: 0.89, // Demo pricing
    realPrice: 8.99, // Production pricing (commented for future)
    period: 'month',
    description: 'Best value for small businesses',
    popular: true,
    features: [
      'Everything in Free Trial',
      'Unlimited teamMembers',
      'Unlimited assets',
      'Unlimited bookings',
      '100 automation actions/month',
      '🤖 AI Auto-Scheduler',
      '💰 Payroll Calculator',
      '📊 Reports & CSV Export',
      '👥 Member Self-Service Portal',
      '📧 Email Support',
      '🔒 Digital Waivers with Device Verification'
    ],
    cta: 'Get Started',
    limits: {
      team: 'unlimited',
      assets: 'unlimited',
      charters: 'unlimited',
      actions: 100
    },
    stripeProductId: 'starter' // Matches backend
  },

  // Enterprise/Custom Plan
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    price: null, // Custom pricing
    period: null,
    description: 'Custom solution for large operations',
    popular: false,
    features: [
      'Everything in Starter',
      'Unlimited everything',
      '∞ Unlimited automation actions',
      '🏢 Multi-location support',
      '👥 Dedicated account manager',
      '📞 Priority support',
      '🎓 Custom onboarding',
      '🔧 Custom integrations',
      '📊 Advanced analytics',
      '🔐 Advanced security'
    ],
    cta: 'Contact Sales',
    limits: {
      team: 'unlimited',
      assets: 'unlimited',
      charters: 'unlimited',
      actions: 'unlimited'
    }
  },

  // Action Top-ups
  actions: {
    topup: {
      amount: 50,
      price: 0.50, // Demo pricing
      realPrice: 4.99 // Production pricing (commented for future)
    }
  }
};

/**
 * Format price for display
 */
export const formatPrice = (price) => {
  if (price === null || price === undefined) return 'Custom';
  if (price === 0) return '$0';
  return `$${price.toFixed(2)}`;
};

/**
 * Get pricing for display
 * @param {string} planId - 'trial', 'starter', or 'enterprise'
 * @param {boolean} useRealPricing - Use production pricing (default: false for demo)
 */
export const getPricing = (planId, useRealPricing = false) => {
  const plan = PRICING_CONFIG[planId];
  if (!plan) return null;

  const price = useRealPricing && plan.realPrice !== undefined 
    ? plan.realPrice 
    : plan.price;

  return {
    ...plan,
    price,
    formattedPrice: formatPrice(price)
  };
};

/**
 * Get all plans for comparison
 */
export const getAllPlans = (useRealPricing = false) => {
  return [
    getPricing('trial', useRealPricing),
    getPricing('starter', useRealPricing),
    getPricing('enterprise', useRealPricing)
  ];
};
